import { t, translate } from '@avalara/bat-apps-shared/i18n';
import type { Connector } from '@avalara/types/index';
import type { History } from 'history';

import { TERMS_LINKS } from '../../../lib/constants';

type Props = {
  myConnectors: Array<Connector>;
  history: History<unknown>;
  inIntegrations: boolean;
  isEligibleToBuy: boolean;
  isFreeTrialCustomer?: boolean;
  onSelectConnector: (arg0: History<unknown> | null | undefined) => void;
  selectedConnector: Connector;
  toggleFooter?: () => void;
};

const SelectButton = ({
  myConnectors,
  history,
  inIntegrations,
  isEligibleToBuy,
  isFreeTrialCustomer,
  onSelectConnector,
  selectedConnector,
  toggleFooter,
}: Props) => {
  const selectedConnectorAlreadyProvisioned = myConnectors.some((conn) => conn.id === selectedConnector.id);

  const supportedAndEligible = (
    <div className={`${inIntegrations ? 'pad-bottom-md' : ''}`}>
      {!inIntegrations && selectedConnector.id === 'a0n4000000ChMxlAAF' && (
        <p>{t('shared.general.connect-with-avalara-api-in-our-sandbox-environment')}</p>
      )}
      {!inIntegrations && (
        <p className='margin-top-xs'>
          By signing up you agree that your use of the service is governed by the&nbsp;
          <a href={TERMS_LINKS.general} target='_blank' rel='noopener noreferrer'>
            {' '}
            terms
          </a>
        </p>
      )}
      <button type='button' className='primary' onClick={() => onSelectConnector(history)}>
        Next
      </button>
    </div>
  );

  const trailingSlash = window.location.href.endsWith('/');
  const contactCam = selectedConnectorAlreadyProvisioned ? (
    <div className='text-center'>
      <span>
        {t('shared.general.this-integration-has-already-been-added-to-your-account')}{' '}
        {inIntegrations ? (
          <button
            type='button'
            style={{ margin: '0 auto' }}
            className={inIntegrations ? 'link block margin-bottom-sm' : 'tertiary'}
            onClick={() => history.push(trailingSlash ? '..' : '.')}
          >
            {t('shared.buttons.manage-integrations')}
          </button>
        ) : null}
      </span>
    </div>
  ) : (
    <div className='text-center'>
      <button
        type='button'
        style={{ margin: '0 auto' }}
        className={inIntegrations ? 'link' : 'tertiary'}
        onClick={toggleFooter}
      >
        {t('shared.buttons.contact-your-account-manager-to-add-this-integration')}
      </button>
    </div>
  );

  // if this is rendered in account, clicking this button takes them to a purchase opportunity (thus we should check availableForOnlineBuy)
  if (
    (inIntegrations && selectedConnector.availableForOnlineBuy && isEligibleToBuy) ||
    // if this is rendered in buydot, then it is (at the moment) always in a trial situation
    (!inIntegrations && selectedConnector.availableForOnlineTrial)
  )
    return supportedAndEligible;

  if (inIntegrations && isFreeTrialCustomer === false && selectedConnector.isAvalaraIncluded === true) {
    let connectorHref: string | undefined;
    if (selectedConnector.installVideo !== null) {
      connectorHref = selectedConnector.installVideo;
    } else if (selectedConnector.userImplementationGuide !== null) {
      connectorHref = selectedConnector.userImplementationGuide;
    }

    if (connectorHref) {
      return (
        <div>
          <p>{t('shared.general.good-news-connector-included-in-your-plan')}</p>
          <p>
            <a href={connectorHref} target='_blank' rel='noopener noreferrer'>
              {t('shared.general.watch-a-demo')}
            </a>
          </p>
        </div>
      );
    }
    return (
      <div>
        <p>
          {translate('shared.general.contact-us-and-well-help-you-get-started', {
            contactLink: (chunks) => (
              <a href='mailto:coach@avalara.com' className={'margin-right-none'}>
                {chunks}
              </a>
            ),
          })}
        </p>
      </div>
    );
  }

  if (inIntegrations && isFreeTrialCustomer === false) return contactCam;

  return (
    <div>
      <p>{t('shared.general.this-connector-isnt-available-online')}</p>
      <p>
        {translate('shared.general.please-contact-us-at-phone', {
          phoneLink: (chunks) => (
            <a href='tel:+18772243650' style={{ color: 'var(--color-blue-darker)' }}>
              {chunks}
            </a>
          ),
        })}
      </p>
    </div>
  );
};

export default SelectButton;
